import { Injectable } from '@angular/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/observable/fromPromise';

@Injectable()
export class RecaptchaService {
  private tokenCache = new Map<string, { 
    token: string; 
    expiry: number; 
    used: boolean;
    requestId?: string;
  }>();
  private pendingPromises = new Map<string, Promise<string>>();
  // Increase token cache duration to 2 minutes (Google recommends <2 min)
  private readonly CACHE_DURATION = 120000;
  private cleanupInterval: any;

  constructor(private recaptchaV3Service: ReCaptchaV3Service) {
    // Run cleanup every minute
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  async executeRecaptcha(action: string, isRefresh: boolean = false): Promise<string> {
    // Clean up before generating new token
    this.cleanup();
    
    const requestId = `${action}_${Date.now()}_${Math.random()}`;
    return this.getNewToken(action, requestId);
  }

  private async getNewToken(action: string, requestId: string): Promise<string> {
    // Check for a valid token in cache first for this action
    const now = Date.now();
    for (const [key, entry] of Array.from(this.tokenCache.entries())) {
      // If we have an unexpired token for the same action that isn't used yet
      if (entry.expiry > now && !entry.used && key.startsWith(action)) {
        // Mark as used only if really necessary - consider removing this line
        // entry.used = true;
        return entry.token;
      }
    }

    this.pendingPromises.delete(action);

    const promise = new Promise<string>((resolve, reject) => {
      const subscription = this.recaptchaV3Service.execute(action)
        .subscribe({
          next: (token) => {
            this.tokenCache.set(requestId, {
              token,
              expiry: Date.now() + this.CACHE_DURATION,
              used: false,
              requestId
            });
            resolve(token);
            subscription.unsubscribe();
          },
          error: (err) => {
            this.tokenCache.delete(requestId);
            this.pendingPromises.delete(action);
            reject(err);
            subscription.unsubscribe();
          },
          complete: () => {
            subscription.unsubscribe();
          }
        });
    });

    this.pendingPromises.set(action, promise);
    
    try {
      const token = await promise;
      const cached = this.tokenCache.get(requestId);
      if (cached) {
        cached.used = true;
        this.tokenCache.set(requestId, cached);
      }
      return token;
    } catch (error) {
      this.tokenCache.delete(requestId);
      throw error;
    }
  }

  private cleanup(): void {
    const now = Date.now();
    
    // Convert to array for older JS iteration
    Array.from(this.tokenCache.keys()).forEach(key => {
      const value = this.tokenCache.get(key);
      if (value && (value.expiry < now || value.used)) {
        this.tokenCache.delete(key);
      }
    });

    // Clean pending promises
    Array.from(this.pendingPromises.keys()).forEach(action => {
      const promise = this.pendingPromises.get(action);
      if (promise && promise.then) {
        promise.then(() => this.pendingPromises.delete(action))
          .catch(() => this.pendingPromises.delete(action));
      }
    });
  }

  ngOnDestroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/core/services/recaptcha.service.ts